/**
 * Brain-team only: a turn that builds a brainwave program over a board, or changes the saved
 * program. The engine reads the board (and the program) itself and gives the builder the program
 * rules, beside the typed text, so the text carries only the person's words however big the
 * program is. Sent on every turn of a build thread: what rides beside the text is not kept.
 */
export interface BrainTeamProgramBuild {
  board_id: string;
  board_name?: string;
  program_id?: string;
  output_name?: string;
  register_ids?: string[];
  /**
   * Of `register_ids`: those whose entries still waiting for approval it reads too, kept apart. Sent
   * only when there are some, or empty when a change stops reading them (the engine keeps the
   * program's own when the turn names none).
   */
  waiting_register_ids?: string[];
  /** Other programs whose newest file the program reads (a report built on a report). */
  program_ids?: string[];
  /** Other boards of the business the program reads besides its own. */
  board_ids?: string[];
  /** The business's trained template (Content → Templates) its document is laid out as. */
  template_id?: string;
  /** A change that lays the document out plainly again: its template is dropped. */
  plain_document?: boolean;
  /** Issued at set times as numbered editions: the builder is told how each issue says which. */
  schedule?: BrainTeamProgramSchedule;
  /** It writes a report for each entry of one of its registers (or each group of its entries). */
  for_each?: BrainTeamProgramForEach;
  /** A change that makes one file again: the report for each entry is dropped. */
  one_file?: boolean;
  /**
   * A change the person asked for by marking places on the program's file: each place with the
   * change wanted there, for the builder to find in what the program writes.
   */
  notes?: BrainTeamProgramNote[];
}

/** A place marked on a program's file, and the change wanted there, as the engine takes it. */
export interface BrainTeamProgramNote {
  change: string;
  quote?: string;
  before?: string;
  after?: string;
  where?: string;
  /** The entry (or group) whose report it is on. */
  entry?: string;
}

const NOTE_FIELDS: ReadonlyArray<[keyof BrainTeamProgramNote, number]> = [
  ['quote', 400],
  ['before', 300],
  ['after', 300],
  ['where', 200],
  ['entry', 200],
];

/** The marked places the engine accepts (on a change only, at most 12, each with its change). */
function safeNotes(value: BrainTeamProgramNote[] | undefined): BrainTeamProgramNote[] {
  return (Array.isArray(value) ? value : [])
    .map((note) => {
      const change = String(note?.change ?? '')
        .trim()
        .slice(0, 600);
      const kept: BrainTeamProgramNote = { change };
      for (const [field, max] of NOTE_FIELDS) {
        const text = String(note?.[field] ?? '')
          .trim()
          .slice(0, max);
        if (text) kept[field] = text;
      }
      return kept;
    })
    .filter((note) => note.change.length > 0)
    .slice(0, 12);
}

/**
 * A report for each entry of a register, as the engine takes it: the register, a column to group by,
 * and `reports: false` when it only sets a few values on each, with no report for any.
 */
export interface BrainTeamProgramForEach {
  register_id: string;
  group_by?: string;
  reports?: false;
  /** Only the entries where a value says certain words: a program it reads sets it, or a column. */
  only?: { program?: string; column: string; says: string };
}

/** When a program is issued as numbered editions, as the engine takes it. */
export interface BrainTeamProgramSchedule {
  every: 'day' | 'week' | 'month' | 'quarter';
  times: string[];
  weekday?: number;
  month_day?: number;
  time_zone: string;
}

const CLOCK_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const TIME_ZONE_RE = /^[A-Za-z_+\-/0-9]{1,64}$/;

/** A schedule the engine accepts (it refuses anything else), or undefined. */
function safeSchedule(value: BrainTeamProgramSchedule | undefined): BrainTeamProgramSchedule | undefined {
  if (!value || !['day', 'week', 'month', 'quarter'].includes(value.every)) return undefined;
  const times = [...new Set((value.times ?? []).map((time) => String(time).trim()))].sort();
  if (!times.length || times.length > 4 || !times.every((time) => CLOCK_TIME_RE.test(time))) return undefined;
  const zone = String(value.time_zone ?? '').trim();
  if (!TIME_ZONE_RE.test(zone)) return undefined;
  const weekday = Number(value.weekday);
  const monthDay = Number(value.month_day);
  return {
    every: value.every,
    times,
    ...(Number.isInteger(weekday) && weekday >= 1 && weekday <= 7 ? { weekday } : {}),
    ...(Number.isInteger(monthDay) && monthDay >= 1 && monthDay <= 28 ? { month_day: monthDay } : {}),
    time_zone: zone,
  };
}

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

/** A report for each entry the engine accepts (it refuses anything else), or undefined. */
function safeForEach(value: BrainTeamProgramForEach | undefined): BrainTeamProgramForEach | undefined {
  const registerId = value?.register_id?.trim() ?? '';
  if (!OBJECT_ID_RE.test(registerId)) return undefined;
  const groupBy = value?.group_by?.trim().slice(0, 100) ?? '';
  const column = value?.only?.column?.replace(/\s+/g, ' ').trim().slice(0, 100) ?? '';
  const says = value?.only?.says?.replace(/\s+/g, ' ').trim().slice(0, 120) ?? '';
  const program = value?.only?.program?.trim() ?? '';
  return {
    register_id: registerId,
    ...(groupBy ? { group_by: groupBy } : {}),
    ...(value?.reports === false ? { reports: false as const } : {}),
    ...(column && says && (!program || OBJECT_ID_RE.test(program))
      ? { only: { ...(program ? { program } : {}), column, says } }
      : {}),
  };
}
/** A template is a skill: its id is the skill's UUID, not an object id. */
export const TEMPLATE_ID_RE = /^[0-9a-f][0-9a-f-]{7,63}$/i;

/** The reference as the engine accepts it (it refuses anything else), or undefined. */
export function safeProgramBuild(
  value: BrainTeamProgramBuild | undefined,
): BrainTeamProgramBuild | undefined {
  const boardId = value?.board_id?.trim() ?? '';
  if (!OBJECT_ID_RE.test(boardId)) return undefined;
  const programId = value?.program_id?.trim() ?? '';
  const registers = [
    ...new Set((value?.register_ids ?? []).map((id) => id.trim()).filter((id) => OBJECT_ID_RE.test(id))),
  ].slice(0, 4);
  const programs = [
    ...new Set((value?.program_ids ?? []).map((id) => id.trim()).filter((id) => OBJECT_ID_RE.test(id))),
  ].slice(0, 3);
  const boards = [
    ...new Set(
      (value?.board_ids ?? []).map((id) => id.trim()).filter((id) => OBJECT_ID_RE.test(id) && id !== boardId),
    ),
  ].slice(0, 4);
  const boardName = value?.board_name?.trim().slice(0, 200) ?? '';
  const outputName = value?.output_name?.trim().slice(0, 255) ?? '';
  const templateId = value?.template_id?.trim() ?? '';
  return {
    board_id: boardId,
    ...(boardName ? { board_name: boardName } : {}),
    ...(OBJECT_ID_RE.test(programId) ? { program_id: programId } : {}),
    ...(outputName ? { output_name: outputName } : {}),
    ...(registers.length > 0 ? { register_ids: registers } : {}),
    ...(Array.isArray(value?.waiting_register_ids)
      ? {
          waiting_register_ids: [
            ...new Set(
              value.waiting_register_ids.map((id) => id.trim()).filter((id) => registers.includes(id)),
            ),
          ],
        }
      : {}),
    ...(programs.length > 0 ? { program_ids: programs } : {}),
    ...(boards.length > 0 ? { board_ids: boards } : {}),
    ...(TEMPLATE_ID_RE.test(templateId) ? { template_id: templateId } : {}),
    ...(value?.plain_document === true ? { plain_document: true } : {}),
    ...(safeSchedule(value?.schedule) ? { schedule: safeSchedule(value?.schedule) } : {}),
    ...(safeForEach(value?.for_each) && value?.one_file !== true
      ? { for_each: safeForEach(value?.for_each) }
      : {}),
    ...(value?.one_file === true ? { one_file: true } : {}),
    // Places marked on the program's file belong to a change of it.
    ...(OBJECT_ID_RE.test(programId) && safeNotes(value?.notes).length > 0
      ? { notes: safeNotes(value?.notes) }
      : {}),
  };
}
